import { Box, Grid, MenuItem, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useMemo, useState, useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import moment from 'moment';
import { COLORS } from '../../../utils/Application_Constants';
import ExpenseTextField from './ExpenseTextField';
import ExpenseDatePicker from './ExpenseDatePicker';
import StatusOption from './StatusOption';
import { ExpenseClassEnum } from './ExpenseClass.enum';
import {
    ExpenseStatusEnum,
    ExpenseStatusValuesEnum,
    ExpenseAdminStatusValuesEnum,
} from './ExpenseStatus.enum';
import { AdminAuthContext } from '../../../Admin/Contexts/AdminAuthContext.js';

const useStyles = makeStyles((theme) => ({
    formHeader: {
        borderBottom: `1px solid ${COLORS.LT_MIDNIGHT25}`,
        paddingBottom: 8,
        marginBottom: 16,
    },
}));

const InternalNotesForm = (props) => {
    const {
        index,
        expense,
        mode,
        statusesAllowed,
        originalExpenseEntries,
        isViewOnly,
        numTabs,
        isReimbursement = false,
    } = props;
    const classes = useStyles();
    const { control, errors, watch, setValue } = useFormContext();
    const adminAuth = useContext(AdminAuthContext);
    const isExpenseAdminUser =
        !adminAuth?.isExpenseSystemAdministratorUser && adminAuth?.isExpenseAdminUser;
    const statuses = useMemo(() => {
        if (mode !== 'pending-cc') {
            let statusExp = null;
            // Find original unmodified expense to base new statuses allowed on
            if (originalExpenseEntries)
                statusExp = originalExpenseEntries.find((entry) => entry.id === expense?.id);
            if (!statusExp) statusExp = expense; // default to live expense
            if (!statusesAllowed || statusesAllowed.length === 0)
                return [{ key: 'New', value: 'New' }];
            const allowedList = statusesAllowed[statusExp?.status ?? 'New'] ?? ['New'];
            let filtered = Object.entries(ExpenseStatusEnum)
                .filter(([key, value]) => allowedList.includes(value.value))
                .map(([key, value]) => value);
            // Expense Admin Status Filtering
            if (isExpenseAdminUser) {
                // Filter to ONLY the Allowed ExpenseAdmin Status List plus the current status
                filtered = filtered.filter(
                    (status) =>
                        Object.values(ExpenseAdminStatusValuesEnum).includes(status.value) ||
                        status.value === statusExp?.status
                );
            }

            if (numTabs > 1) {
                filtered = filtered.filter(
                    (status) => status.value !== ExpenseStatusValuesEnum.NeedMoreInformation
                );
            }
            return filtered;
        }
    }, [statusesAllowed, expense]);

    const [curStatus, setCurStatus] = useState(expense.status);
    // Get the original status from the originalExpenseEntries
    const originalStatus = useMemo(() => {
        if (expense.id && originalExpenseEntries?.length > 0) {
            const originalExpense = originalExpenseEntries.find((entry) => entry.id === expense.id);
            return originalExpense ? originalExpense.status : expense.status;
        }
        return expense.status; // Fallback to current status if no original entry is found
    }, [expense.id, originalExpenseEntries, expense.status]);
    // Track the current status being edited
    const currentStatus = watch(`expenseEntries[${index}].status`);
    // Compute isPosted based on original status and current transient state
    const isPosted = useMemo(
        () =>
            originalStatus === 'Posted' ||
            (currentStatus === 'OnHoldReimbursement' && originalStatus === 'Posted'),
        [originalStatus, currentStatus]
    );
    const isPendingSubmission = expense.status === 'PendingSubmission';
    const canEditStatus = useMemo(() => {
        if (mode === 'payment') {
            return true;
        }
        if (isPosted) {
            // Only allow editing Posted expenses if the user is an Expense System Administrator
            if (mode !== 'cc') {
                return adminAuth?.isExpenseSystemAdministratorUser;
            }
            return !isViewOnly;
        }
        return (
            !isPendingSubmission &&
            (adminAuth?.isExpenseSystemAdministratorUser || adminAuth?.isExpenseAdminUser)
        );
    }, [isPosted, isPendingSubmission, adminAuth]);

    const MAX_LENGTH = 1000;
    // MA-1914 Remove delivery date until end accounting system is in place
    // const handleDateChange = (e) => {
    //     if (e && e.startOf('day')) {
    //         setValue(
    //             `expenseEntries[${index}].deliveryDate`,
    //             e.startOf('day').format('YYYY-MM-DD')
    //         );
    //     } else {
    //         setValue(`expenseEntries[${index}].deliveryDate`, e);
    //     }
    // };

    // const showHideDeliveryDate = (newStatus) => {
    //     // Need to trigger re-render of date control
    //     setCurStatus(newStatus);
    // };

    return (
        <div>
            <Typography variant="h5" component="h5" className={classes.formHeader}>
                Internal Notes
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Controller
                        control={control}
                        name={`expenseEntries[${index}].notes`}
                        defaultValue={expense.notes || ''}
                        render={({ onChange, value }) => (
                            <ExpenseTextField
                                multiline
                                inputProps={{ maxLength: MAX_LENGTH }}
                                minRows={3}
                                id={`notes${index}`}
                                onChange={onChange}
                                value={value}
                                disabled={
                                    isViewOnly ||
                                    (isPosted && mode !== 'vault' && isViewOnly) ||
                                    (isPendingSubmission && mode !== 'payment') ||
                                    currentStatus === ExpenseStatusValuesEnum.Abandoned
                                }
                                label="LT Internal Notes"
                                helperText={value && `${value.length}/${MAX_LENGTH}`}
                                placeholder="Internal notes here"
                                slotProps={{
                                    input: {
                                        sx: {
                                            height: 'auto', // Allow auto height
                                            fontSize: '14px',
                                            padding: '10px 12px !important',
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        marginTop: '0',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        transform: 'translate(0, 1.5px) scale(0.75) !important',
                                    },
                                    '& .MuiInputBase-root::before': {
                                        borderBottom: 'none !important',
                                    }, // Removes default underline
                                    '& .MuiInputBase-root::after': {
                                        borderBottom: 'none !important',
                                    }, // Prevents focus underline
                                }}
                            />
                        )}
                    />
                </Grid>
                {mode === 'pending-cc' || (expense?.id ?? 0) === 0 ? null : (
                    <>
                        <Grid item xs={12} md={6}>
                            <Controller
                                control={control}
                                name={`expenseEntries[${index}].status`}
                                defaultValue={expense.status}
                                rules={{
                                    required:
                                        expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                            ? 'Status is required'
                                            : false,
                                }}
                                render={({ onChange, value }) => (
                                    <ExpenseTextField
                                        id={`status${index}`}
                                        select
                                        onChange={(e) => {
                                            onChange(e);
                                            // showHideDeliveryDate(e.target.value);
                                        }}
                                        value={value}
                                        label="Status"
                                        required={
                                            expense?.status !== ExpenseStatusValuesEnum.Abandoned
                                        }
                                        disabled={
                                            (isViewOnly || !canEditStatus) && !isReimbursement
                                        }
                                        error={Boolean(errors.expenseEntries?.[index]?.status)}
                                        helperText={errors.expenseEntries?.[index]?.status?.message}
                                    >
                                        <MenuItem value="" disabled>
                                            Select a status
                                        </MenuItem>
                                        {statuses.map((status, index) => (
                                            <MenuItem
                                                key={status.value}
                                                value={status.value}
                                                id={`status-menu-option-${index}`}
                                            >
                                                <StatusOption
                                                    status={status.value}
                                                    label={status.UI}
                                                />
                                            </MenuItem>
                                        ))}
                                    </ExpenseTextField>
                                )}
                            />
                        </Grid>
                        {/* {curStatus != ExpenseStatusValuesEnum.Approved ? null : (
                            <Grid item xs={12} md={6}>
                                <Controller
                                    control={control}
                                    name={`expenseEntries[${index}].deliveryDate`}
                                    defaultValue={expense.deliveryDate || null}
                                    rules={
                                        isPosted
                                            ? {}
                                            : {
                                                  required: 'Delivery date is required',
                                                  validate: {
                                                      invalid: (value) =>
                                                          !value ||
                                                          moment(value).isValid() ||
                                                          'Invalid date',
                                                      maxDate: (value) =>
                                                          !value ||
                                                          moment(value).isSameOrBefore(
                                                              moment().add(7, 'days')
                                                          ) ||
                                                          'Max date reached',
                                                      minDate: (value) =>
                                                          !value ||
                                                          moment(value).isSameOrAfter(
                                                              moment().startOf('day')
                                                          ) ||
                                                          'Min date reached',
                                                  },
                                              }
                                    }
                                    render={({ onChange, value }) => (
                                        <ExpenseDatePicker
                                            onChange={(e) => {
                                                onChange(e);
                                                handleDateChange(e);
                                            }}
                                            inputProps={{ disableUnderline: true }}
                                            value={value ? moment(value) : null}
                                            id="delivery-date"
                                            label="Delivery Date"
                                            minDate={moment()}
                                            maxDate={moment().add(7, 'days')}
                                            required
                                            disabled={isViewOnly || isPosted || isPendingSubmission}
                                            parentTextFieldProps={{
                                                error: Boolean(
                                                    errors.expenseEntries?.[index]?.deliveryDate
                                                ),
                                                helperText:
                                                    errors.expenseEntries?.[index]?.deliveryDate
                                                        ?.message,
                                            }}
                                            parentSx={{
                                                '& .MuiInputBase-root': {
                                                    borderColor: !errors.expenseEntries?.[index]
                                                        ?.deliveryDate
                                                        ? 'default'
                                                        : `${COLORS.LT_PEPPER} !important`,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        )} */}
                        {currentStatus != ExpenseStatusValuesEnum.OnHoldReimbursement ? null : (
                            <Grid item xs={12}>
                                <Controller
                                    control={control}
                                    name={`expenseEntries[${index}].onHoldComment`}
                                    defaultValue={expense.onHoldComment || ''}
                                    rules={{
                                        required: 'On Hold reason is required',
                                    }}
                                    render={({ onChange, value }) => (
                                        <ExpenseTextField
                                            multiline
                                            inputProps={{ maxLength: 200 }}
                                            minRows={3}
                                            id={`on-hold-comment${index}`}
                                            onChange={onChange}
                                            value={value}
                                            label="On Hold Reason"
                                            placeholder="Enter the on hold reason here"
                                            required
                                            error={Boolean(
                                                errors.expenseEntries?.[index]?.onHoldComment
                                            )}
                                            helperText={
                                                errors.expenseEntries?.[index]?.onHoldComment
                                                    ?.message
                                            }
                                        />
                                    )}
                                />
                            </Grid>
                        )}
                    </>
                )}
            </Grid>
        </div>
    );
};

export default InternalNotesForm;
